import { useCallback } from 'react';
import styled from 'styled-components';

import { colors, spacing } from 'ui/theme';
import { DoubleArrow, DownArrow, UpArrow } from 'ui/icons';
import { useSortingContext } from 'utils/contexts/sorting';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { button } from 'ui/theme/typography';
import { useSetSortDirection } from './hooks';

import { LIST_COLUMNS } from '../../types';

export const HeaderButton = styled.button`
  ${button}
  background: none;
  border: none;
  padding: ${spacing.half} 0;
  outline: none;
  text-align: left;
  color: ${colors.textPrimary};
  display: flex;
  align-items: center;

  &[data-active-sort='true'] {
    color: ${colors.brandPurple}
  }

  span {
    margin-right: ${spacing.quarter};
  }
`;

type Column = {
  id: LIST_COLUMNS;
  label: string;
  isSortable: boolean;
  initialSortDirection?: SORT_DIRECTIONS;
};

type HeaderRowCellProps = {
  column: Column;
};

export const HeaderRowCell = ({ column }: HeaderRowCellProps) => {
  const { setSortBy, sortBy, sortDirection } = useSortingContext();
  const setSortDirection = useSetSortDirection();
  const { isSortable } = column;
  const isAscendingSort = sortDirection === SORT_DIRECTIONS.ASCENDING;
  const isActiveSortColumn = sortBy === column.id;

  const handleClick = useCallback(() => {
    if (isActiveSortColumn) {
      setSortDirection(isAscendingSort ? SORT_DIRECTIONS.DESCENDING : SORT_DIRECTIONS.ASCENDING);
    } else if (isSortable) {
      setSortBy(column.id);
      setSortDirection(column.initialSortDirection || SORT_DIRECTIONS.DESCENDING);
    }
  }, [
    setSortDirection,
    setSortBy,
    isSortable,
    column.id,
    isAscendingSort,
    isActiveSortColumn,
    column.initialSortDirection,
  ]);

  return (
    <HeaderButton
      data-sort={isSortable}
      data-active-sort={isActiveSortColumn}
      as={isSortable ? 'button' : 'div'}
      data-test={`column-header-${column.id}`}
      key={column.id}
      onClick={handleClick}
    >
      <span>{column.label}</span>
      {isSortable && !isActiveSortColumn && <DoubleArrow isColorInherited />}
      {isSortable && isActiveSortColumn && isAscendingSort && <UpArrow isColorInherited />}
      {isSortable && isActiveSortColumn && !isAscendingSort && <DownArrow isColorInherited />}
    </HeaderButton>
  );
};
